import { useParams } from 'react-router';

import { useDashboard } from '@/features/Dashboard/api/queries';
import { getTabContent } from '@/features/Dashboard/lib/transformers';
import type { DashboardTab, MarketApps } from '@/features/Dashboard/types';

/**
 * Returns the full configuration (all tabs) for the current dashboard.
 * Based on the dashboardId from URL.
 */
export const useCurrentDashboardConfig = (): DashboardTab | undefined => {
  const { data: dashboards } = useDashboard();
  const { dashboardId } = useParams<{ dashboardId: string }>();

  if (!dashboards || !dashboardId) {
    return undefined;
  }

  const dashboardName = decodeURIComponent(dashboardId);
  return dashboards[dashboardName];
};

/**
 * Returns the configuration (MarketApps) for the current active tab of the current dashboard.
 * Based on the dashboardId and tabName from URL.
 */
export const useCurrentDashboardTabConfig = (): MarketApps | undefined => {
  const dashboardConfig = useCurrentDashboardConfig();
  const { tabName } = useParams<{ tabName: string }>();

  if (!dashboardConfig) {
    return undefined;
  }

  // If no tabName in URL, fallback to the first tab
  const effectiveTabName = tabName ? decodeURIComponent(tabName) : Object.keys(dashboardConfig)[0];

  if (!effectiveTabName || !dashboardConfig[effectiveTabName]) {
    return undefined;
  }

  return getTabContent(dashboardConfig[effectiveTabName]);
};
